import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import classNames from "classnames";

import Card from "@components/zenith/ui/Card";
import { useAppContext } from "@context/AppContext";
import { useAllNotification } from "@hooks/query/useAllNotifications";
import { formatSchemeDay } from "@utils/schemeDay";
import { transformNotification } from "@utils/transformNotification";

const RecentNotificationsWidget: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { loggedInInfo } = useAppContext();
  const walletId = loggedInInfo?.userDetails?.walletId;

  // The API returns the newest first.
  const { data, isLoading, isFetching, isError } = useAllNotification({
    params: { recipientWalletId: walletId, limit: 3 },
    enabled: Boolean(walletId),
  });
  const notifications = data?.data ?? [];

  return (
    <Card
      title={t("zenith.dashboard.recent_notifications")}
      busy={isFetching}
      action={
        <Link
          to="/zenith/notifications"
          className="text-xs font-semibold text-primary-1 hover:underline"
        >
          {t("common.view_all")}
        </Link>
      }
    >
      {!isLoading && notifications.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-gray-500">
          {/* A failed refresh keeps the notifications last loaded, so only notifications that never loaded are unavailable. */}
          {isError && !data
            ? t("zenith.dashboard.notifications_unavailable")
            : t("notification.no_notifications")}
        </p>
      ) : null}
      <ul>
        {notifications
          .map((item: any) => transformNotification(item, !item.viewedAt))
          .map((item: ReturnType<typeof transformNotification>) => (
            <li
              key={item.id}
              className="border-b border-gray-100 last:border-0"
            >
              <Link
                to={`/zenith/notifications?id=${item.id}`}
                className="flex items-start gap-3 px-5 py-3 hover:bg-gray-50"
              >
                <span
                  className={classNames(
                    "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                    item.isNew ? "bg-red-500" : "bg-gray-200",
                  )}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    {item.name}
                  </p>
                  <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-gray-500">
                    {item.subject}
                  </p>
                </div>
                <span className="whitespace-nowrap text-xs text-gray-500">
                  {formatSchemeDay(item.date)}
                </span>
              </Link>
            </li>
          ))}
      </ul>
    </Card>
  );
};

export default RecentNotificationsWidget;
